// Taking one slot out of the day.
//
// The pattern says Kate teaches 3:00, 4:00 and 5:00. This Tuesday the farrier
// is coming at 5. That is not a change to the pattern — next Tuesday is fine —
// and it is not a day off. It is one hour that isn't going to happen.
//
// The reason is the whole value here. A slot that vanishes with no explanation
// looks like a bug in the calendar; "Farrier" looks like a barn. So the reason
// is offered as one tap from the four a barn actually gives, and typing your
// own is always available rather than hidden behind "Other".

import { useState } from 'react';
import { fmtDate } from '../../lib/dates';

// The four that cover most of it. Not a closed list — the field underneath
// takes anything, and these are just the ones worth not typing.
const COMMON = ['Lunch', 'Farrier', 'Vet', 'Meeting'];

function fmtTime(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

export default function BlockSlotDialog({ slot, trainerName, busy, error, onConfirm, onClose }) {
  const [reason, setReason] = useState('Lunch');

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onConfirm(reason.trim() || null);
        }}
        className="bg-white w-full sm:max-w-md rounded-t-xl sm:rounded-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Block this slot"
      >
        <div className="flex items-start gap-3 px-5 py-4 border-b border-ink-100">
          <div className="min-w-0 flex-1">
            <h2 className="font-medium text-lg leading-snug">Block this slot</h2>
            <p className="text-sm text-ink-500">
              {fmtDate(slot.date, 'dayMedium')} · {fmtTime(slot.startTime)} – {fmtTime(slot.endTime)}
              {trainerName ? ` · ${trainerName}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-md text-ink-500 hover:bg-ink-100 text-xl leading-none shrink-0"
          >
            ×
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            {COMMON.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setReason(r)}
                aria-pressed={reason === r}
                className={
                  'px-3 py-1.5 rounded-full text-sm border ' +
                  (reason === r
                    ? 'bg-hunter text-white border-hunter'
                    : 'bg-white text-ink-700 border-ink-200 hover:border-ink-300')
                }
              >
                {r}
              </button>
            ))}
          </div>

          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-ink-400 mb-1" htmlFor="block-reason">
              Reason
            </label>
            <input
              id="block-reason"
              className="input w-full"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Farrier"
              maxLength={60}
            />
          </div>

          <p className="text-xs text-ink-400">
            This hour stops being bookable on this day only. The pattern is unchanged, so next week
            still has it.
          </p>

          {error && (
            <p className="rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700">
              {error}
            </p>
          )}
        </div>

        <div className="px-5 py-4 border-t border-ink-100 flex justify-end gap-2">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Blocking…' : 'Block it'}
          </button>
        </div>
      </form>
    </div>
  );
}
